/**
 * Floor Store - Manages floors within parking lots
 * Persists to localStorage: map-editor-floors
 *
 * CSV/DXF integration:
 * - Persisted CSV data stored per floor (floor.mapData.csv)
 * - DXF layer data storage per floor
 * - In-memory CSV runtime state managed by csvStore (separate store)
 */

import { create } from 'zustand';
import type { FloorState } from './types';
import { loadInitialState } from './persistence';
import { createFloorActions } from './floorActions';
import { createCsvActions } from './csvActions';
import { createDxfActions } from './dxfActions';
import { useCsvRuntimeStore } from '../../../map-editor-features/csv/model/csvStore';
import { syncStorage } from '../../lib/indexedDBStorage';

export const useFloorStore = create<FloorState>((set, get) => {
  const initialState = loadInitialState();

  // Re-load state after IndexedDB init completes, but ONLY if the store
  // hasn't been modified yet (e.g., by API data or floor operations).
  // Without this guard, the onReady callback fires asynchronously and
  // overwrites the current project's correctly-loaded data with stale
  // floor data from a previous session, causing the wrong project's map
  // to appear after a brief flash of the correct one.
  syncStorage.onReady(() => {
    // If any floor operations have happened since store creation
    // (add/delete/update from API sync or useProjectStateReset deleting stale floors),
    // the store already has the correct data — don't overwrite with stale IndexedDB data.
    const currentState = get();
    if (currentState.floors !== initialState.floors) {
      return;
    }
    const freshState = loadInitialState();
    if (freshState.floors.length > 0) {
      set({ floors: freshState.floors, currentFloor: freshState.currentFloor });
    }
  });

  return {
    ...initialState,
    isFloorSwitching: false,

    ...createFloorActions(set, get),
    ...createCsvActions(set, get),
    ...createDxfActions(set, get),
  };
});

// Sync currentFloor → csvStore so CSV runtime tracks the active floor.
let _prevFloor: string | null = useFloorStore.getState().currentFloor;
useCsvRuntimeStore.getState().setCurrentFloor(_prevFloor);
useFloorStore.subscribe((state) => {
  if (state.currentFloor !== _prevFloor) {
    _prevFloor = state.currentFloor;
    useCsvRuntimeStore.getState().setCurrentFloor(state.currentFloor);
  }
});

// Re-export all types
export type {
  CSVData,
  DXFLayerData,
  DXFData,
  MapData,
  MapDataPatch,
  Floor,
  FloorState,
} from './types';

// Re-export branded types that consumers import from this module
export type { FloorId, ProjectId } from '../../types/branded';
